import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  selectClientsIsLoading,
  selectSession,
  selectSessionAdvisor,
  selectSessionIsLoading,
} from '../model/selectors';
import { advisorRequiredFields } from '@client/util/useAdvisorProfile';
import { SessionType } from '@shared/SessionType';
import { FullScreenLoadingOverlay } from '@client';
import { useAdvisorOnboarding } from '@client/hooks/useAdvisorOnboarding';

export function useAdvisorNavigation() {
  const navigate = useNavigate();
  const advisor = useSelector(selectSessionAdvisor);
  const location = useLocation();

  // Helper to preserve admin mode query params across navigation
  const getAdminModeParams = (): string => {
    const searchParams = new URLSearchParams(location.search);
    const adminMode = searchParams.get('adminMode');
    const advisorId = searchParams.get('advisorId');

    if (!adminMode) return '';

    const params = new URLSearchParams();
    params.set('adminMode', adminMode);
    if (advisorId) params.set('advisorId', advisorId);
    return `?${params.toString()}`;
  };

  const navigateToActionRequiredView = () => {
    const actionRequiredPath = `/advisors/${advisor.advisorId}/account-action-required`;
    navigate(actionRequiredPath);
  };

  const navigateToClientsDashboard = () => {
    const clientsPath = `/advisors/${advisor.advisorId}/clients`;
    navigate(clientsPath);
  };

  const navigateToWelcomeDashboard = () => {
    const welcomePath = `/advisors/${advisor.advisorId}/welcome`;
    navigate(welcomePath);
  };

  const navigateToAdvisorProfile = (tab?: 'profile' | 'communication' | 'defaults') => {
    const advisorProfilePath = `/advisors/${advisor.advisorId}/settings`;
    if (tab) {
      navigate(`${advisorProfilePath}?tab=${tab}`);
    } else {
      navigate(advisorProfilePath);
    }
  };

  // const navigateToOpportunitiesDashboard = () => {
  //   const opportunitiesPath = `/advisors/${advisor.advisorId}/opportunities`;
  //   navigate(opportunitiesPath);
  // };

  const navigateToSponsorshipDashboard = () => {
    const sponsorshipPath = `/advisors/${advisor.advisorId}/sponsorship`;
    navigate(sponsorshipPath);
  };

  const navigateToOnboarding = (toCompletion = false) => {
    const onboardingPath = toCompletion
      ? `/advisors/${advisor.advisorId}/onboarding/completion`
      : `/advisors/${advisor.advisorId}/onboarding/welcome`;
    navigate(onboardingPath);
  };

  const navigateToSeatsDashboard = () => {
    const seatsPath = `/advisors/${advisor.advisorId}/seats${getAdminModeParams()}`;
    navigate(seatsPath);
  };

  const navigateToIntegrations = () => {
    const integrationsPath = `/advisors/${advisor.advisorId}/integrations`;
    navigate(integrationsPath);
  };

  const navigateToTeamDashboard = () => {
    const teamPath = `/advisors/${advisor.advisorId}/team${getAdminModeParams()}`;
    navigate(teamPath);
  };

  const navigateToUsageDashboard = () => {
    const usagePath = `/advisors/${advisor.advisorId}/usage${getAdminModeParams()}`;
    navigate(usagePath);
  };

  const isBasePath = (path: string) => {
    return (
      !!path.match(/^\/advisors\/[a-z0-9]{4}-[a-z0-9]{3}$/) ||
      path === `/advisors` ||
      path === `/advisors/`
    );
  };

  return {
    navigateToActionRequiredView,
    navigateToClientsDashboard,
    navigateToWelcomeDashboard,
    navigateToAdvisorProfile,
    // navigateToOpportunitiesDashboard,
    navigateToSponsorshipDashboard,
    navigateToOnboarding,
    navigateToSeatsDashboard,
    navigateToIntegrations,
    navigateToTeamDashboard,
    navigateToUsageDashboard,
    isBasePath,
    currentPath: location.pathname,
  };
}

interface RouteGuardProps {
  children: React.ReactNode;
}

export function AdvisorDashboardRouteGuard({ children }: RouteGuardProps) {
  const isSessionLoading = useSelector(selectSessionIsLoading);
  const { sessionType, advisorHasClients } = useSelector(selectSession);
  const advisor = useSelector(selectSessionAdvisor);
  const isClientsLoading = useSelector(selectClientsIsLoading);
  const {
    navigateToActionRequiredView,
    navigateToClientsDashboard,
    navigateToWelcomeDashboard,
    navigateToOnboarding,
    navigateToSeatsDashboard,
    isBasePath,
    currentPath,
    navigateToAdvisorProfile,
  } = useAdvisorNavigation();

  const {
    completedSteps,
    isLoading: isOnboardingLoading,
    isComplete: isOnboardingComplete,
    shouldShowOnboarding,
  } = useAdvisorOnboarding(advisor?.advisorId);

  const isAdvisorSessionType =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  useEffect(() => {
    if (isSessionLoading || isClientsLoading || isOnboardingLoading || !isOnboardingComplete) {
      return;
    }

    const missingRequiredFields = advisorRequiredFields.filter(
      field => !advisor[field as keyof AdvisorProfile],
    );

    if (
      // force navigate to the account action required page (no subscription or unverified)
      [SessionType.advisorUnverifiedAccount, SessionType.advisorNoSubscription].includes(
        sessionType,
      ) &&
      !currentPath.includes('account-action-required') &&
      !currentPath.includes('team') &&
      !currentPath.includes('seats')
    ) {
      navigateToActionRequiredView();
    } else if (
      // force navigate to seats page (has subscription but inactive seat)
      sessionType === SessionType.advisorInactiveSubscription &&
      !currentPath.includes('seats') &&
      !currentPath.includes('team')
    ) {
      navigateToSeatsDashboard();
    } else if (
      // force navigate to the advisor profile/settings
      isAdvisorSessionType &&
      !currentPath.includes('settings') &&
      missingRequiredFields.length > 0
    ) {
      navigateToAdvisorProfile();
    } else if (
      // navigate to onboarding after login if advisor has < 5 clients and hasn't completed onboarding
      isAdvisorSessionType &&
      shouldShowOnboarding &&
      isBasePath(currentPath)
    ) {
      const hasAnyProgress = completedSteps.length > 0;
      navigateToOnboarding(hasAnyProgress);
    } else if (
      // force navigate to the clients dashboard
      isAdvisorSessionType &&
      advisorHasClients &&
      (isBasePath(currentPath) ||
        currentPath.includes('account-action-required') ||
        currentPath.includes('welcome'))
    ) {
      navigateToClientsDashboard();
    } else if (
      // force navigate to the welcome dashboard
      isAdvisorSessionType &&
      !advisorHasClients &&
      (isBasePath(currentPath) ||
        currentPath.includes('account-action-required') ||
        currentPath.includes('clients'))
    ) {
      navigateToWelcomeDashboard();
    }
  }, [
    isSessionLoading,
    isClientsLoading,
    isOnboardingLoading,
    isOnboardingComplete,
    sessionType,
    currentPath,
    advisor,
    advisorHasClients,
    shouldShowOnboarding,
    isAdvisorSessionType,
    isBasePath,
    navigateToActionRequiredView,
    navigateToAdvisorProfile,
    navigateToClientsDashboard,
    navigateToWelcomeDashboard,
    navigateToOnboarding,
    navigateToSeatsDashboard,
    completedSteps,
  ]);

  return (
    <>
      <FullScreenLoadingOverlay isLoading={isSessionLoading} />
      {children}
    </>
  );
}
